// Content from the client-supplied quiz-result-content.json ("Starting Over - What's My Next Step").
// Option VALUES (ids) exactly match the keys in that file; friendly labels are display-only.
// q3_duration and q4_readiness are lead_scoring_only — never used to select copy.

export const QUESTIONS = [
  {
    id: "q1",
    key: "q1",
    title: "What's the big thing you're stuck on?",
    subtitle: "Pick the one that weighs on you the most right now.",
    options: [
      { id: "career_change", label: "Career change", detail: "Stepping back, switching paths, or finding fulfilling work" },
      { id: "relationships", label: "Relationships", detail: "Empty nest, changing partnerships, family dynamics" },
      { id: "health_body", label: "Health & body", detail: "Energy, vitality, and how you feel in your own skin" },
      { id: "purpose_identity", label: "Purpose & identity", detail: "The quiet question: 'Who am I now?'" },
      { id: "money_security", label: "Money & security", detail: "Financial runway, retirement, balancing dreams with safety" },
      { id: "other", label: "Something else / a mix", detail: "Hard to name — but you know it's there" },
    ],
  },
  {
    id: "q2",
    key: "q2",
    title: "What's really stopping you?",
    subtitle: "This is usually the conversation you have with yourself at 2 AM.",
    options: [
      { id: "no_clarity", label: "I don't know where to start", detail: "Too many options, or step one feels paralyzing" },
      { id: "fear_of_failure", label: "Scared of failing", detail: "The stakes feel higher now than they did at 25" },
      { id: "no_time", label: "No time or energy", detail: "Work, caregiving, or plain exhaustion eats the day" },
      { id: "no_support", label: "No support around me", detail: "The people close to you don't quite get it" },
      { id: "disbelief", label: "I don't believe it's possible for me", detail: "It feels like your window may have closed" },
    ],
  },
  {
    id: "q3",
    key: "q3",
    title: "How long have you felt this way?",
    subtitle: "Be gentle with your answer. Naming the timeline is part of releasing it.",
    options: [
      { id: "just_started", label: "Just started", detail: "The last few weeks" },
      { id: "few_months", label: "A few months", detail: "It's been quietly building" },
      { id: "over_a_year", label: "Over a year", detail: "You've been going in circles" },
      { id: "years", label: "Years", detail: "You've put yourself on the backburner" },
    ],
  },
  {
    id: "q4",
    key: "q4",
    title: "On a scale of 1–5, how ready are you to actually do something about it?",
    subtitle: "There's no wrong number. 1 means 'just exploring' — 5 means 'this month.'",
    isScale: true,
    options: [
      { id: "1", label: "1", detail: "Just gathering thoughts" },
      { id: "2", label: "2", detail: "Tired of the status quo" },
      { id: "3", label: "3", detail: "Willing to test a small step" },
      { id: "4", label: "4", detail: "Actively hungry for change" },
      { id: "5", label: "5", detail: "All-in on my next act" },
    ],
  },
];

// Matched primarily on q2_blocker; the step is filled from q1_domain.
export const PATTERNS = {
  no_clarity: {
    id: "no_clarity",
    label: "Don't know where to start",
    opener:
      "Sisterfriend, this one's not about willpower. You're not stuck because you're lazy or lost — you're stuck because the goal is too big to see the first stair step. Big changes always look like a cliff until somebody hands you the first small foothold.",
    bridge:
      "That's one step. You don't need the whole staircase mapped today — you need this one. The full 30-day map is exactly what we build together, live, in your Reinvention Kickstarter session.",
    steps: {
      career_change: "Open LinkedIn and change one line of your headline — not the whole profile, just one line.",
      relationships: "Send one text to the person you've been meaning to reach out to. Just \"thinking of you,\" nothing more.",
      health_body: "Put on real shoes and walk to the end of your driveway and back. That's it.",
      purpose_identity: "Write one sentence that starts with \"I used to love...\" and finish it.",
      money_security: "Log into one account you've been avoiding and just look. Don't fix anything yet.",
      other: "Pick the smallest piece of the big thing and do that one piece today.",
    },
  },
  fear_of_failure: {
    id: "fear_of_failure",
    label: "Scared of failing",
    opener:
      "Here's what I know after building at 61 with nothing but a story and some land: fear doesn't go away before you start. It goes away because you started. So we're not waiting for the fear to leave.",
    bridge:
      "That's the whole trick. Pick a step so small fear can't win. Thirty days of steps like that adds up to a different life. That's what we map out together in your session.",
    steps: {
      career_change: "Tell one person — just one — \"I'm thinking about a change.\" Watch, the world won't end.",
      relationships: "Say the true thing out loud to yourself in the mirror, before you say it to them.",
      health_body: "Try the new thing for ten minutes. Ten minutes can't fail.",
      purpose_identity: "Do the thing badly, on purpose, just to prove bad is survivable.",
      money_security: "Ask one question out loud to someone who'd know the answer.",
      other: "Do the smallest, least risky version of the scary thing, today.",
    },
  },
  no_time: {
    id: "no_time",
    label: "No time",
    opener:
      "You don't need more hours. You need one that already belongs to you, pointed somewhere new.",
    bridge:
      "Fifteen minutes, once. That's today's job. In the Reinvention Kickstarter, we build the other twenty-nine days around the time you actually have, not the time you wish you had.",
    steps: {
      career_change: "Fifteen minutes tonight: update one line on your resume.",
      relationships: "Fifteen minutes: write the text you keep composing in your head.",
      health_body: "Fifteen minutes: a walk, a stretch, water instead of your usual — pick one.",
      purpose_identity: "Fifteen minutes: write three things that used to make you feel alive.",
      money_security: "Fifteen minutes: pull up one account and write down the real number.",
      other: "Fifteen minutes, one piece of the goal, no more.",
    },
  },
  no_support: {
    id: "no_support",
    label: "No support",
    opener:
      "Nobody around you has to believe in this yet. You just need one person who does — and today, that's me.",
    bridge:
      "You built this far without a cheering section. Step two through thirty is where having someone in your corner actually matters. That's what a Reinvention Kickstarter session is for.",
    steps: {
      career_change: "Reach out to one former coworker you trusted. Just to reconnect, no agenda.",
      relationships: "Tell one safe person the real version of what's going on.",
      health_body: "Do the first walk or workout alone — it counts even with no one watching.",
      purpose_identity: "Write your own permission slip: \"I'm allowed to want this.\" Keep it somewhere you'll see it.",
      money_security: "Make the first call to someone who can actually help, even if it feels exposing.",
      other: "Take the first step alone. The support comes in step two.",
    },
  },
  disbelief: {
    id: "disbelief",
    label: "Don't believe it's possible",
    opener:
      "I grew up in the inner city. I built a house on 21 acres at 61. I'm not telling you that to impress you — I'm telling you so the word \"impossible\" has a harder time holding up in this conversation.",
    bridge:
      "Belief doesn't come first. Evidence does. This step is your first piece of evidence. The next 29 days build the case — that's what we do together in your session.",
    steps: {
      career_change: "Find one person your age who changed careers and read their story. Proof exists — go find it.",
      relationships: "Remember one time you already survived a hard conversation. You have evidence.",
      health_body: "Do the smallest version of the physical thing today, and notice: your body did it.",
      purpose_identity: "List three things you've already reinvented in your life without calling it that.",
      money_security: "Find one number that's better than you assumed. There usually is one.",
      other: "Do the smallest possible version of the impossible thing. Impossible rarely survives a small enough dose.",
    },
  },
};

export const getResult = (q1, q2) => {
  const pattern = PATTERNS[q2] || PATTERNS.no_clarity;
  return {
    id: pattern.id,
    label: pattern.label,
    opener: pattern.opener,
    step: pattern.steps[q1] || pattern.steps.other,
    bridge: pattern.bridge,
  };
};

export const CTA = {
  line: "This is step one of thirty. The rest of the roadmap comes from a live 90-minute 1:1 session.",
  buttonText: "Book your Reinvention Kickstarter",
  url: "https://www.grayrootssociety.com/workwithme-4a41707d",
};
